/**
 * Candidacy Kanban View
 * 
 * Uses the global Kanban component with candidacy-specific configuration.
 * All column definitions come from candidacyPhaseConfig.ts
 */

import { useState, useEffect } from 'react';
import { Application } from "../candidacies";
import { CandidacyCard } from './CandidacyCard';
import { Kanban } from 'shared/components/Kanban';
import { candidaciesKanbanConfig } from 'shared/components/Kanban/configs/candidaciesKanbanConfig';
import { type CandidacyStatus, getStatusLabel } from '../candidacyPhaseConfig';
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import { CandidateEmailModal } from "components/candidates/email";
import { ArchiveCandidateModal } from "components/candidates/archiveCandidateModal";
import { CandidateInfo } from "components/candidates/types";

interface CandidacyKanbanViewProps {
  candidacies: Application[];
  onUpdate: () => void;
  onCandidacyClick: (candidacy: Application) => void;
}

/**
 * Determine whether a candidacy is an Invited entity (vs an Application).
 * Prefers the backend `isInvited` flag — status alone is ambiguous because
 * "reminder-sent" and "forwarded-to-broker" exist in both tables. Falls back to
 * status for legacy rows without the flag.
 */
const isInvitedCandidacy = (c: Application): boolean => {
  if (typeof c.isInvited === "boolean") return c.isInvited;
  return c.status === "notify" || c.status === "invited" || c.status === "reminder-sent";
};

export const CandidacyKanbanView = ({ 
  candidacies, 
  onUpdate,
  onCandidacyClick
}: CandidacyKanbanViewProps) => {
  const { session } = useSession();
  
  // Local state for optimistic updates
  const [localCandidacies, setLocalCandidacies] = useState(candidacies);
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedCandidateForEmail, setSelectedCandidateForEmail] = useState<Application | null>(null);
  
  // Delete/Archive modal state
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [selectedCandidateForArchive, setSelectedCandidateForArchive] = useState<Application | null>(null);
  
  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Update local state when prop changes (but only if it's actually different)
  // This prevents resetting optimistic updates
  useEffect(() => {
    // Only update if the data has actually changed (e.g., after a full refresh)
    // We check by comparing the stringified version to avoid unnecessary resets
    const currentIds = localCandidacies.map(c => `${c.id}-${c.status}`).sort().join(',');
    const newIds = candidacies.map(c => `${c.id}-${c.status}`).sort().join(',');
    
    if (currentIds !== newIds) {
      setLocalCandidacies(candidacies);
    }
  }, [candidacies, localCandidacies]);

  /**
   * Handle drop from the Kanban component
   */
  const handleDrop = async (candidacyId: string | number, newStatus: CandidacyStatus, oldStatus: CandidacyStatus) => {
    // OPTIMISTIC UPDATE: Update local state immediately
    setLocalCandidacies((prev) => 
      prev.map(c => 
        c.id === candidacyId 
          ? { ...c, status: newStatus }
          : c
      )
    );

    // Make API call in background
    try {
      let endpoint = '';
      
      // Map status to appropriate endpoint
      if (newStatus === 'approved') {
        endpoint = `${API_URL}/rental-appointment/approve/${candidacyId}`;
      } else if (newStatus === 'rejected') {
        endpoint = `${API_URL}/rental-appointment/reject/${candidacyId}`;
      } else {
        // For other statuses, use a generic status update endpoint
        endpoint = `${API_URL}/rental-appointment/update-status/${candidacyId}`;
      }

      const response = await fetchWithAuth(
        endpoint,
        {
          method: 'PUT' as const,
          headers: { "Content-Type": "application/json" } as Record<string, string>,
          body: (newStatus !== 'approved' && newStatus !== 'rejected') 
            ? JSON.stringify({ status: newStatus }) 
            : undefined,
        },
        session
      );

      if (!response.ok) {
        throw new Error("Failed to update candidacy status");
      }

      toast.success(`Kandidatuur status bijgewerkt naar "${getStatusLabel(newStatus)}"`);
    } catch (error: any) {
      console.error("Error updating candidacy status:", error);
      toast.error(`Fout bij bijwerken status: ${error.message || 'Onbekende fout'}`);
      
      // ROLLBACK: Revert local state on error
      setLocalCandidacies((prev) => 
        prev.map(c => 
          c.id === candidacyId 
            ? { ...c, status: oldStatus }
            : c
        )
      );
      
      // Refetch to ensure data consistency
      onUpdate();
    }
  };

  // Email handlers
  const handleEmailClick = (candidacy: Application, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCandidateForEmail(candidacy);
    setShowEmailModal(true);
  };

  const handleEmailSuccess = () => {
    setShowEmailModal(false);
    setSelectedCandidateForEmail(null);
    onUpdate();
  };

  // Delete/Archive handlers
  const handleDeleteClick = (candidacy: Application, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCandidateForArchive(candidacy);
    setShowArchiveModal(true);
  };

  const handleArchiveConfirm = async (reason: string) => {
    if (!selectedCandidateForArchive) return;

    setIsProcessing(true);

    try {
      const isInvited = isInvitedCandidacy(selectedCandidateForArchive);

      if (isInvited) {
        // Delete invited candidate
        const response = await fetchWithAuth(
          `${API_URL}/rental-appointment/delete-invited/${selectedCandidateForArchive.id}`,
          { method: "DELETE" },
          session
        );

        if (response.ok) {
          toast.success("Kandidaat succesvol verwijderd!");
          setShowArchiveModal(false);
          setSelectedCandidateForArchive(null);
          onUpdate(); // Refresh data
        } else {
          toast.error("Fout bij verwijderen kandidaat");
        }
      } else {
        // Archive application
        const response = await fetchWithAuth(
          `${API_URL}/rental-appointment/archive-application/${selectedCandidateForArchive.id}`,
          { 
            method: "POST",
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ reason })
          },
          session
        );

        if (response.ok) {
          toast.success("Kandidaat succesvol gearchiveerd!");
          setShowArchiveModal(false);
          setSelectedCandidateForArchive(null);
          onUpdate(); // Refresh data
        } else {
          toast.error("Fout bij archiveren kandidaat");
        }
      }
    } catch (error) {
      console.error("Error archiving/deleting candidate:", error);
      toast.error(isInvitedCandidacy(selectedCandidateForArchive)
        ? "Fout bij verwijderen kandidaat"
        : "Fout bij archiveren kandidaat");
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Render a candidacy card with email and archive functionality
   */
  const renderCandidacyCard = (candidacy: Application) => {
    return (
      <CandidacyCard
        candidacy={candidacy}
        onClick={() => onCandidacyClick(candidacy)}
        onEmail={handleEmailClick}
        onDelete={handleDeleteClick}
      />
    );
  };

  return (
    <div className="overflow-hidden">
      <Kanban
        config={candidaciesKanbanConfig}
        items={localCandidacies}
        renderCard={renderCandidacyCard}
        onDrop={handleDrop}
        onCardClick={onCandidacyClick}
      />

      {/* Email Modal - Only render when open */}
      {showEmailModal && selectedCandidateForEmail && (
        <CandidateEmailModal
          isOpen={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            setSelectedCandidateForEmail(null);
          }}
          onSuccess={handleEmailSuccess}
          candidate={selectedCandidateForEmail as CandidateInfo}
        />
      )}

      {/* Archive/Delete Modal - Only render when open */}
      {showArchiveModal && selectedCandidateForArchive && (
        <ArchiveCandidateModal
          isOpen={showArchiveModal}
          onClose={() => {
            setShowArchiveModal(false);
            setSelectedCandidateForArchive(null);
          }}
          onConfirm={handleArchiveConfirm}
          candidate={selectedCandidateForArchive as CandidateInfo}
          isLoading={isProcessing}
          isInvited={selectedCandidateForArchive ? isInvitedCandidacy(selectedCandidateForArchive) : false}
        />
      )}
    </div>
  );
};

